import { faBars } from "@fortawesome/pro-solid-svg-icons/faBars";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Disclosure } from "@headlessui/react";
import { useRouter } from "next/router";
import { Link } from "~/components/Link";
import { SearchLayout } from "~/modules/search/components/SearchLayout";
import { EMPTY_USER_STATE } from "~/modules/user/store/consts";
import { PlanTypes, User } from "~/modules/user/store/types";
import { Routes } from "../../routes";
import { getButtonClassName } from "../Button";
import { ButtonVariants } from "../Button";
import { WordmarkLogo } from "../Logo";
import { MobileNav } from "../SidebarLayout/MobileNav";
import { CRMForms } from "./CRMForms";
import { UserMenu } from "./UserMenu";
import { ThemeToggle } from "../ThemeToggle";
import { ThemeToggleHotkey } from "../ThemeToggle/ThemeToggleHotkey";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { UserPendingInvitations } from "./UserPendingInvitations";
import { PlanBadge } from "./PlanBadge";
import { NavbarWallet } from "~/modules/swap";
import { NavigationUserEvents } from "~/modules/analytics/events/navigation";
import { DataApiCTA } from "./DataApiCTA";
import { cn } from "~/lib/utils";
import { getHasApiAccess } from "~/modules/user/util/getHasApiAccess";

// // // //

/**
 * Render top-level navbar for the application
 */
export function Navbar(props: { user: User | undefined }) {
    const router = useRouter();
    const { user } = props;

    const isLoggedIn = user !== undefined && user.id !== EMPTY_USER_STATE.id;

    // Define a boolean that checks if the user has API access
    const hasApiAccess: boolean = getHasApiAccess(
        user?.organization.plan.type ?? PlanTypes.FREE,
    );

    return (
        <Disclosure
            as="nav"
            className="bg-sidebar border-b shadow-xs sticky w-full z-250 select-none top-0"
        >
            {({ open }) => (
                <>
                    <div className="relative flex h-16 justify-between ml-2 mr-2 md:mr-6">
                        <div className="absolute inset-y-0 left-0 flex items-center md:hidden">
                            {/* Mobile menu button */}
                            <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-slate-100 hover:text-muted-foreground-hover focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-blue-500">
                                <span className="sr-only">Open main menu</span>
                                {open ? (
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        className="block h-6 w-6"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={faBars}
                                        className="block h-6 w-6"
                                        aria-hidden="true"
                                    />
                                )}
                            </Disclosure.Button>
                        </div>
                        <div className="flex flex-1 pl-8 items-center justify-center md:flex-none md:pl-2 md:items-stretch md:justify-start">
                            <div className="flex shrink-0 justify-center items-center w-auto md:mx-4">
                                <Link
                                    href={Routes.home}
                                    analyticsLabel={
                                        NavigationUserEvents.navbarIconClick
                                    }
                                >
                                    <WordmarkLogo className="w-32 text-foreground" />
                                </Link>
                                <div className="hidden 2xl:flex gap-3 items-center ml-3">
                                    <div className="h-[40px] border-l border-slate-500"></div>
                                    <div className="font-light text-slate-500 text-xs w-[160px]">
                                        The registry for tokenized real-world
                                        assets
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Render SearchLayout (GlobalSearch + CommandPalette) */}
                        <div className="hidden md:flex min-w-0 flex-1 justify-start">
                            <div className="mt-3 ml-4 w-full max-w-160">
                                <SearchLayout />
                            </div>
                        </div>

                        <>
                            {/* <div className="absolute inset-y-0 right-0 hidden lg:flex gap-2 items-center pr-2 md:static md:inset-auto md:ml-6 md:pr-0"> */}
                            {/* <FeedbackPopover /> */}
                            {/* </div> */}

                            <div className="flex items-center gap-2 pr-2 md:gap-3 md:ml-6 md:pr-0">
                                {/* Connected trading wallet (renders nothing when disconnected) */}
                                <NavbarWallet />
                                <div className="hidden lg:flex items-center gap-2">
                                    <Link
                                        href={Routes.citations}
                                        analyticsLabel={
                                            NavigationUserEvents.navbarMediaCitationsClick
                                        }
                                        className={cn(
                                            getButtonClassName({
                                                size: "sm",
                                                rounded: "md",
                                                variant:
                                                    ButtonVariants.navbarGhost,
                                                className: "shrink-0",
                                            }),
                                            "hidden lg:inline-flex",
                                        )}
                                    >
                                        Press &amp; Citations
                                    </Link>

                                    <DataApiCTA hasApiAccess={hasApiAccess} />

                                    <CRMForms
                                        buttonText="Book Demo"
                                        buttonClassName={cn(
                                            getButtonClassName({
                                                size: "sm",
                                                rounded: "md",
                                                variant:
                                                    ButtonVariants.navbarGhost,
                                                className:
                                                    "group shrink-0 gap-1.5",
                                            }),
                                            "hidden xl:block",
                                        )}
                                    />
                                </div>

                                {!isLoggedIn && (
                                    <>
                                        <Link
                                            href={Routes.login}
                                            analyticsLabel={
                                                NavigationUserEvents.navbarLoginClick
                                            }
                                            className={cn(
                                                getButtonClassName({
                                                    size: "sm",
                                                    rounded: "md",
                                                    variant:
                                                        ButtonVariants.navbarGhost,
                                                    className:
                                                        "shrink-0 gap-1.5",
                                                }),
                                                "border-transparent hidden md:inline-flex",
                                            )}
                                        >
                                            Log in
                                        </Link>
                                        <Link
                                            href={Routes.register}
                                            analyticsLabel={
                                                NavigationUserEvents.navbarRegisterClick
                                            }
                                            className={getButtonClassName({
                                                size: "sm",
                                                rounded: "md",
                                                variant:
                                                    ButtonVariants.navbarSecondary,
                                                className: "shrink-0 gap-1.5",
                                            })}
                                        >
                                            Sign up
                                        </Link>
                                    </>
                                )}

                                {isLoggedIn && (
                                    <div className="flex items-center gap-2 md:gap-3 md:ml-1">
                                        <FeatureFlag
                                            feature={
                                                FeatureNames.enableExperimentalFeatures
                                            }
                                        >
                                            <ThemeToggle />
                                        </FeatureFlag>

                                        <ThemeToggleHotkey user={user} />
                                        <PlanBadge user={user} />
                                        <UserPendingInvitations user={user} />
                                        <UserMenu user={user} />
                                    </div>
                                )}
                            </div>
                        </>
                    </div>

                    {/* Render mobile SearchLayout (doesn't render CommandPalette) */}
                    <div className="flex md:hidden h-15 justify-between">
                        <div className="border-t px-3 py-2 w-full">
                            <SearchLayout isMobile={true} />
                        </div>
                    </div>

                    <Disclosure.Panel className="md:hidden">
                        <MobileNav
                            pathname={router.pathname}
                            isLoggedIn={isLoggedIn}
                        />
                    </Disclosure.Panel>
                </>
            )}
        </Disclosure>
    );
}
